import * as THREE from 'three';

import {
  Axial,
  axialKey,
  axialToWorld,
  neighbor,
  roundAxial,
  worldToAxial,
} from '../map/HexCoordinates';
import { CORNERS, elevationToY } from '../map/HexMetrics';
import { HexCellData } from '../map/HexSurface';
import { colorFor } from '../players/PlayerMarkers';
import { BuildingLook, disposeShapes, lookFor, scaffoldLook } from './BuildingShapes';
import { Catalogue, buildingById } from './Catalogue';
import { Placed } from './Placed';
import { footprint } from './Site';

/**
 * Buildings standing on the map.
 *
 * The silhouettes come from `BuildingShapes`, shared with the ghost the cursor
 * carries in build mode: what is previewed and what is put up have to be the
 * same object, or the preview stops meaning anything.
 *
 * Colour is the **owner's**, from the same hash their pawn uses: the shape says
 * what it is, the colour says whose it is. Two hashes would put the same person
 * in two colours.
 *
 * Height comes from the terrain the client happens to hold. A building may
 * stand on ground that has not arrived yet; it keeps the last height it had
 * until `refresh` puts it where it belongs, exactly as pawns do.
 */

/**
 * O ile rusztowanie jest większe od tego, co pod nim stoi.
 *
 * Ma otaczać budynek, a nie stać w nim: przy jedynce ściany wychodziłyby przez
 * pomosty tam, gdzie model jest najszerszy u góry.
 */
const SCAFFOLD_MARGIN = 1.12;

/**
 * Czy to jeszcze plac budowy.
 *
 * Po tym, co powiedział serwer, a nie po własnym zegarze — zerowanie `readyIn`
 * jest tym, jak ten plik zapisuje u siebie, że budowa się skończyła.
 */
/**
 * O ile obrócić model, żeby jego front patrzył tam, gdzie ma.
 *
 * **Przyjęte: front modelu wskazuje +Z.** Tak leżał pierwszy model, który dało
 * się zmierzyć (`stone-border-1`), i tak zakłada ten obrót. Gdyby budynki
 * wyszły bokiem, poprawką jest **ta jedna stała** — dodana do wszystkich naraz
 * — a nie grzebanie w każdym pliku z osobna.
 */
const MODEL_FRONT = 0;

/**
 * W którą stronę patrzy ten budynek, w radianach.
 *
 * **Krawędź z drogą, gdy serwer ją zapisał; inaczej los z identyfikatora.**
 * Los jest deterministyczny i to jest w nim najważniejsze: ten sam budynek
 * wygląda tak samo u wszystkich patrzących i po każdym przeładowaniu strony.
 * Budynki sprzed tej zmiany nie mają zapisanego kierunku i dostają obrót tą
 * właśnie drogą — bez migracji danych, bo brak wartości ma tu własne znaczenie.
 *
 * Kąt liczy się do **środka krawędzi**, nie do narożnika: droga wchodzi na pole
 * przez krawędź, więc dom zwrócony ku jej środkowi stoi do niej frontem.
 */
/**
 * Kąt do środka krawędzi `edge` — ta sama arytmetyka, którą obraca się dom ku
 * drodze, wyjęta osobno, bo pyta o nią teraz także oś skrzydeł.
 */
function edgeAngle(edge: number): number {
  const from = CORNERS[edge % 6];
  const to = CORNERS[(edge + 1) % 6];
  // Obrót o θ przenosi (0,0,1) na (sin θ, 0, cos θ), więc kąt do punktu bierze
  // się jako `atan2(x, z)`.
  return MODEL_FRONT + Math.atan2((from.x + to.x) / 2, (from.z + to.z) / 2);
}

/**
 * Kąt, pod którym leży oś skrzydeł: ku **środkowi sąsiedniego pola**, a nie ku
 * środkowi krawędzi. Doki stoją na polach, więc to ich środki wyznaczają linię.
 */
export function alongAxis(axis: number): number {
  const side = neighbor({ q: 0, r: 0 }, axis % 3);
  const { x, z } = axialToWorld(side.q, side.r);
  return MODEL_FRONT + Math.atan2(x, z);
}

function facingOf(placed: Placed): number {
  // **Budynek ze skrzydłami leży wzdłuż swojej osi, i to ona rozstrzyga obrót.**
  // Doki stoczni zajmują dwa konkretne pola, więc bryła obrócona wedle samego
  // `facing` rozminęłaby się z polami, które budynek naprawdę zajmuje — pomosty
  // leżałyby w poprzek sąsiadów, których nikomu nie zabrał.
  if (placed.axis !== undefined) return turnFor(placed.axis, placed.facing);

  if (placed.facing !== undefined) return edgeAngle(placed.facing);

  // Z identyfikatora, bo to jedyna rzecz o tym budynku, która się nie zmienia.
  let hash = 0;
  for (const character of placed.id) {
    hash = (hash * 31 + character.charCodeAt(0)) % 100003;
  }
  return MODEL_FRONT + (hash / 100003) * Math.PI * 2;
}

/**
 * Jak obrócić bryłę budynku, który leży wzdłuż osi.
 *
 * **Front stoi w poprzek osi, nie wzdłuż niej** — i to była pomyłka warta
 * zapisania, bo wyglądała na działającą. Doki leżą wzdłuż osi, więc bryła
 * obrócona frontem wzdłuż niej patrzy prosto na własny pomost, a tyłem na wodę.
 * Obrót o ±90° od osi kładzie doki tam, gdzie mają leżeć, i zwraca halę ku
 * morzu.
 *
 * Kompozycję układa się wobec tego **z osią wzdłuż ±X**: przy tym obrocie
 * kierunek +X ląduje dokładnie na osi, a +Z — na wodzie.
 *
 * **Jedna funkcja, bo pytają o to dwie rzeczy**: budynek, który stoi, i duch pod
 * kursorem. Duch liczący to sam przestałby być podglądem w dniu, w którym ta
 * reguła się zmieni — a właśnie się zmieniła.
 */
export function turnFor(axis: number, facing: number | undefined): number {
  const along = alongAxis(axis);
  const across = [along - Math.PI / 2, along + Math.PI / 2];
  if (facing === undefined) return across[0];

  // Która z dwóch przeciwnych stron: ta bliższa wodzie. Wybór jest między dwoma
  // obrotami różniącymi się o π, więc przekręca budynek, a nie przenosi doki
  // gdzie indziej.
  const water = edgeAngle(facing);
  const gap = (turn: number): number =>
    Math.abs(Math.atan2(Math.sin(water - turn), Math.cos(water - turn)));
  return gap(across[0]) <= gap(across[1]) ? across[0] : across[1];
}

function onSite(placed: Placed): boolean {
  return placed.readyIn > 0;
}

/**
 * Co widać na polu: bryła, rusztowanie, albo jedno na drugim.
 *
 * **Jedno miejsce na tę regułę**, bo pytają o nią trzy: stawianie bryły,
 * porównanie „czy to dalej wygląda tak samo" przy podmianie i wysokość, na
 * której wieszają się dymki i paski. Trzy kopie rozjechałyby się przy pierwszej
 * zmianie, a każda z osobna wyglądałaby na działającą.
 *
 * **Rozbudowa pokazuje to, co stoi** — obóz podnoszony do wioski jest obozem,
 * dopóki czas nie upłynie, więc pod rusztowaniem stoi obóz. **Stawianie od zera
 * nie ma czego pokazać**: nie ma poprzedniego szczebla, jest samo rusztowanie
 * na pustym polu.
 */
function shownOf(key: string, placed: Placed): {
  body?: BuildingLook;
  scaffold?: BuildingLook;
  /** Skala rusztowania — z wysokości tego, co pod nim. */
  scale: number;
  /** Gdzie kończy się to wszystko, w jednostkach świata. */
  height: number;
} {
  const raising = placed.raisingTo !== undefined;
  const scaffold = onSite(placed) ? scaffoldLook() : undefined;
  // **Bryła znika wyłącznie wtedy, gdy jest co postawić zamiast niej.** Bez
  // pliku rusztowania plac budowy wygląda tak, jak wyglądał, zanim ten plik
  // istniał — a nie jak puste pole, którego nie da się kliknąć.
  const body = scaffold && !raising ? undefined : lookFor(key, placed.level);

  // **Nigdy mniejsze od siebie samego.** Rusztowanie jest zmierzone tą samą
  // miarą, co budynki, więc jedynka znaczy „na całe pole"; rośnie tylko wtedy,
  // gdy pod nim stoi coś wyższego. Bez tego dolnego ograniczenia rozbudowa
  // rodzaju, który ma jeszcze zastępczą sylwetkę — a te bywają płaskie jak
  // zaorana płachta pola — dostałaby rusztowanie wielkości kamyka.
  const scale =
    scaffold && body
      ? Math.max(1, (body.height * SCAFFOLD_MARGIN) / Math.max(scaffold.height, 1e-6))
      : 1;

  return {
    body,
    scaffold,
    scale,
    height: Math.max(body?.height ?? 0, scaffold ? scaffold.height * scale : 0),
  };
}

/**
 * Co stoi w polu, dla rzeczy, które się do budynków dostawiają.
 *
 * Punkty przychodzą **policzone w świecie**, a nie jako bryła plus offset:
 * pytający — ogień i dym — mają wtedy gotową pozycję i żadne z nich nie musi
 * wiedzieć ani o skali pliku, ani o tym, w którą stronę ten dom patrzy.
 */
export interface Standing {
  id: string;
  key: string;
  /** Ziemia pod budynkiem. */
  position: THREE.Vector3;
  /** Skąd świeci ogień. Puste, gdy modelowi nikt tego nie ustawił. */
  hearth: THREE.Vector3[];
  /** Skąd idzie dym. Puste jak wyżej. */
  smoke: THREE.Vector3[];
  site: boolean;
}

/**
 * Punkt bryły przeniesiony tam, gdzie ta bryła stoi.
 *
 * Liczone **na żądanie, a nie zapamiętane przy postawieniu**, i to jest wybór:
 * budynek przesiada się na właściwą wysokość, ilekroć dojedzie teren pod nim
 * (`seat`), i zmienia bryłę, gdy skończy się budować (`reshape`) — zapamiętany
 * punkt musiałby o obu tych chwilach wiedzieć, a ta, o której by zapomniał,
 * zostawiłaby ognisko wiszące nad polem, którego nikt by nie powiązał z awansem
 * sprzed pół godziny. Rachunek to dwa mnożenia na punkt.
 */
function turned(point: THREE.Vector3, turn: number, at: THREE.Vector3): THREE.Vector3 {
  const sin = Math.sin(turn);
  const cos = Math.cos(turn);
  return new THREE.Vector3(
    at.x + point.x * cos + point.z * sin,
    at.y + point.y,
    at.z - point.x * sin + point.z * cos
  );
}

/**
 * Skąd wziąć sztandar właściciela — ta sama funkcja, którą dostają pionki.
 */
export type BannerSource = (
  owner: string,
  version: number,
  height: number
) => Promise<THREE.Object3D | null>;

/** Wysokość drzewca sztandaru na budynku, w jednostkach świata. */
const STAFF_HEIGHT = 0.35;

export class BuildingField {
  readonly object = new THREE.Group();

  private readonly materials = new Map<number, THREE.MeshStandardMaterial>();
  private bannerSource?: BannerSource;
  /** Postawione sztandary, po budynku. */
  private readonly flags = new Map<string, THREE.Object3D>();

  private readonly standing = new Map<
    string,
    { placed: Placed; mesh: THREE.Object3D; picks: THREE.Mesh[]; since: number }
  >();

  /**
   * Cells with something on them, by cell key.
   *
   * A set beside the map, because the props of a chunk ask this once per cell
   * — a thousand times per chunk built — and a scan over every building would
   * turn drawing a wood into work that grows with how much has been built.
   */
  private readonly occupied = new Set<number>();

  /**
   * Pola, na które wychodzą budynki z sąsiednich pól: klucz pola → budynki.
   * Obok — odwrotność, żeby przy podmianie bryły wiedzieć, co zdjąć.
   */
  private readonly covered = new Map<number, Set<string>>();
  private readonly covering = new Map<string, Axial[]>();

  /**
   * Zmienił się zestaw pól pod wystającymi budynkami — teren tam trzeba
   * przeliczyć. Ustawia `Game`, tą samą drogą, którą idzie `settle`.
   */
  onGround: (cells: Axial[]) => void = () => {};

  constructor(
    private readonly world: Map<number, HexCellData>,
    private readonly catalogue: Catalogue,
    /**
     * Everything the mouse can hit, shared with the terrain.
     *
     * Buildings are in it so that pointing at a roof means pointing at the
     * building. The ray otherwise passes straight through the silhouette into
     * the ground behind it, and on a tall hut that ground is a **different
     * cell** — the same error this project already measured when the ray was
     * allowed through the sea to the bottom.
     */
    private readonly pickTargets: THREE.Object3D[]
  ) {
    this.object.name = 'buildings';
  }

  /** Everything in the area just loaded. Answers with what was actually new. */
  addAll(list: Placed[]): Placed[] {
    return list.filter((placed) => this.add(placed));
  }

  /**
   * Pola, które te budynki wyrównują pod sobą — u stoczni trzy na budynek.
   *
   * Pytane przy każdym `settle`, bo wyrównywanie idzie **za polem**, a nie za
   * budynkiem: dok pominięty tutaj zostałby kawałkiem zbocza pod pomostem,
   * widocznym dopiero po przeładowaniu strony.
   */
  ground(list: Placed[]): Axial[] {
    return list.flatMap((placed) => this.cells(placed));
  }

  /**
   * One building, by identity.
   *
   * Idempotent because it arrives twice by design: the placement answers over
   * HTTP and the same event comes back over the socket, the way a step does.
   */
  add(placed: Placed): boolean {
    if (this.standing.has(placed.id)) return false;

    const { mesh, picks } = this.build(placed);
    this.object.add(mesh);
    // **Do zbioru celów trafiają siatki, nie grupa.** Raycaster jest wołany
    // nierekurencyjnie (`HexPicker`), więc grupa nigdy nie zostałaby trafiona,
    // a budynek z modelu przestałby dawać się kliknąć.
    this.pickTargets.push(...picks);

    // When this client heard about it, which is what the countdown runs from.
    const held = { placed, mesh, picks, since: performance.now() };
    this.standing.set(placed.id, held);
    // **Wszystkie pola, na których ten budynek stoi** — u stoczni także doki.
    // Zajętość czyta stąd wyrównanie terenu, rekwizyty, panel pola i reguła
    // miejsca sąsiada, więc dok pominięty tutaj byłby polem, na którym rośnie
    // las i da się postawić chatę.
    for (const spot of this.cells(placed)) this.occupied.add(axialKey(spot.q, spot.r));
    this.seat(placed, mesh);
    this.raiseBanner(placed.id, held, placed);
    this.cover(placed);
    return true;
  }

  /**
   * Do jakiego poziomu obniżyć teren tego pola — najniższy z budynków, które
   * na nie wychodzą. Pole zabudowane nie obniża się nigdy: stoi na nim coś,
   * co ma swoją platformę.
   */
  lowered(at: Axial): number | undefined {
    const key = axialKey(at.q, at.r);
    const ids = this.covered.get(key);
    if (!ids || this.occupied.has(key)) return undefined;

    let lowest: number | undefined;
    for (const id of ids) {
      const placed = this.standing.get(id)?.placed;
      if (!placed) continue;
      const data = this.world.get(axialKey(placed.q, placed.r));
      if (!data) continue;
      lowest = lowest === undefined ? data.elevation : Math.min(lowest, data.elevation);
    }
    return lowest;
  }

  /**
   * Pola pod wystającymi budynkami, których teren nie wie jeszcze o obniżeniu —
   * bo pole budynku dojechało później niż to, na które budynek wychodzi.
   */
  staleGround(): Axial[] {
    const stale: Axial[] = [];
    for (const [key, ids] of this.covered) {
      const data = this.world.get(key);
      if (!data || ids.size === 0) continue;
      const cell = this.covering.get(ids.values().next().value as string)?.find(
        (one) => axialKey(one.q, one.r) === key
      );
      if (cell && data.lowered !== this.lowered(cell)) stale.push(cell);
    }
    return stale;
  }

  /**
   * Zapisz, na które pola wychodzi ten budynek, i daj znać o tych, które się
   * zmieniły.
   *
   * Bryła to ta **szczebla, który stoi**, także pod rusztowaniem — teren
   * przygotowuje się razem z placem budowy, a nie dopiero po nim.
   */
  private cover(placed: Placed): void {
    const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
    const look = lookFor(key, placed.level);
    const turn = facingOf(placed);
    const at = axialToWorld(placed.q, placed.r);
    const origin = new THREE.Vector3(at.x, 0, at.z);

    const now: Axial[] = look.overhang.map((center) => {
      const spot = turned(center, turn, origin);
      const fractional = worldToAxial(spot.x, spot.z);
      return roundAxial(fractional.q, fractional.r);
    });
    const before = this.covering.get(placed.id) ?? [];
    const same =
      now.length === before.length && now.every((cell, i) => cell.q === before[i].q && cell.r === before[i].r);
    if (same) return;

    for (const cell of before) {
      const ids = this.covered.get(axialKey(cell.q, cell.r));
      ids?.delete(placed.id);
      if (ids?.size === 0) this.covered.delete(axialKey(cell.q, cell.r));
    }
    for (const cell of now) {
      const cellKey = axialKey(cell.q, cell.r);
      const ids = this.covered.get(cellKey) ?? new Set<string>();
      ids.add(placed.id);
      this.covered.set(cellKey, ids);
    }
    if (now.length > 0) this.covering.set(placed.id, now);
    else this.covering.delete(placed.id);

    this.onGround([...before, ...now]);
  }

  /**
   * What stands on a cell and where a label over it belongs.
   *
   * The top of what is drawn, from the same `shownOf` the progress bars use:
   * two things hanging over one building at two heights would read as two
   * buildings. Na placu budowy jest to szczyt **rusztowania**, bo to ono tam
   * stoi.
   *
   * The countdown is recomputed from this client's own clock, the way `raising`
   * does it — the number the server sent was true when it said it, and a
   * tooltip open for ten seconds would otherwise keep saying so.
   */
  under(at: Axial): { placed: Placed; anchor: THREE.Vector3 } | undefined {
    if (!this.occupied.has(axialKey(at.q, at.r))) return undefined;

    const now = performance.now();
    // Dok jest częścią stoczni, więc kursor nad dokiem pyta o stocznię — ten
    // sam wybór, co w `placedAt` i co po tamtej stronie drutu. Pole własne
    // najpierw, bo o nie pyta się najczęściej.
    const standing = this.standingOn(at);
    for (const { placed, mesh, since } of standing) {
      const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
      return {
        placed: {
          ...placed,
          readyIn: Math.max(0, placed.readyIn - (now - since) / 1000),
        },
        anchor: new THREE.Vector3(
          mesh.position.x,
          mesh.position.y + shownOf(key, placed).height,
          mesh.position.z
        ),
      };
    }
    return undefined;
  }

  /**
   * Ród albo sztandar właściciela się zmienił: dopisz to do wszystkich jego
   * budynków i przestaw na nich sztandary.
   */
  setOwner(owner: string, banner: number | undefined, house: string | undefined): void {
    for (const [id, held] of this.standing) {
      if (held.placed.owner !== owner) continue;
      held.placed.house = house;
      if (held.placed.banner === banner) continue;
      held.placed.banner = banner;
      this.raiseBanner(id, held, held.placed);
    }
  }

  /**
   * Skąd brać sztandary. Budynki, które już stoją, dostają swoje od razu.
   */
  useBanners(source: BannerSource): void {
    this.bannerSource = source;
    for (const [id, held] of this.standing) this.raiseBanner(id, held, held.placed);
  }

  /**
   * Postaw sztandar właściciela na budynku — albo zdejmij ten, który stoi.
   *
   * **Dziecko bryły**, więc obraca się razem z nią (budynek frontem do drogi)
   * i znika razem z nią przy podmianie. Punkt `banner` jest w jednostkach
   * świata względem podstawy — ta sama przestrzeń, co ognisko i komin.
   * Bryła bez punktu sztandaru nie dostaje; plac budowy też nie, bo nie ma
   * na nim jeszcze bryły.
   */
  private raiseBanner(
    id: string,
    held: { mesh: THREE.Object3D },
    placed: Placed
  ): void {
    const old = this.flags.get(id);
    if (old) {
      old.removeFromParent();
      this.flags.delete(id);
    }

    const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
    const point = shownOf(key, placed).body?.banner[0];
    const version = placed.banner;
    if (!point || version === undefined || !this.bannerSource) return;

    const mesh = held.mesh;
    void this.bannerSource(placed.owner, version, STAFF_HEIGHT).then((flag) => {
      // Między pytaniem a odpowiedzią bryła mogła się zmienić albo sztandar.
      const now = this.standing.get(id);
      if (!flag || !now || now.mesh !== mesh || now.placed.banner !== version) return;
      this.flags.get(id)?.removeFromParent();
      flag.position.copy(point);
      mesh.add(flag);
      this.flags.set(id, flag);
    });
  }

  /** Budynek po identyfikatorze — dla panelu, który zna id, a nie pole. */
  find(id: string): Placed | undefined {
    return this.standing.get(id)?.placed;
  }

  /**
   * Budynek z czasem budowy liczonym **teraz**, zegarem tego klienta — tak jak
   * w `under`. `find` i `placedAt` oddają liczbę z chwili, w której serwer ją
   * wysłał, więc panel otwarty na placu budowy stał na niej w miejscu.
   */
  fresh(id: string): Placed | undefined {
    const held = this.standing.get(id);
    if (!held) return undefined;
    return {
      ...held.placed,
      readyIn: Math.max(0, held.placed.readyIn - (performance.now() - held.since) / 1000),
    };
  }

  /**
   * Szczyt sylwetki po identyfikatorze — tam, gdzie zaczepia się to, co nad
   * budynkiem.
   *
   * **Ta sama wysokość, co w `under` i w `raising`**, i to jest cały powód, dla
   * którego liczy ją ten plik, a nie ten, kto pyta: dymek, pasek budowy i
   * chmurka surowców wiszące nad jedną bryłą na trzech wysokościach czytałyby
   * się jak trzy różne budynki.
   *
   * Nic, gdy budynku tu nie ma — a to jest zwykły przypadek, nie błąd: wóz
   * rusza z zakładu, który mógł zostać za krawędzią wczytanego świata, i wtedy
   * po prostu nie ma nad czym rysować.
   */
  anchorOf(id: string): THREE.Vector3 | undefined {
    const held = this.standing.get(id);
    if (!held) return undefined;

    const key = buildingById(this.catalogue, held.placed.kind)?.key ?? 'unknown';
    return new THREE.Vector3(
      held.mesh.position.x,
      held.mesh.position.y + shownOf(key, held.placed).height,
      held.mesh.position.z
    );
  }

  /**
   * Co stoi w polu: rodzaj, ziemia pod tym i czy to jeszcze plac budowy.
   *
   * **Dla rzeczy dostawianych do budynków**, a nie dla mechaniki — dziś czytają
   * to ogniska. Zwracany jest rodzaj, a nie identyfikator katalogu, bo pytający
   * poznaje budynek po tym, **czym jest**, a nie po numerze w pliku; i ziemia
   * pod nim, a nie szczyt sylwetki, bo to, co się dostawia, stoi przy budynku,
   * a nie na dachu.
   */
  standingAll(): Standing[] {
    const now = performance.now();
    const all: Standing[] = [];

    for (const { placed, mesh, since } of this.standing.values()) {
      const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
      const look = lookFor(key, placed.level);
      const turn = facingOf(placed);

      all.push({
        id: placed.id,
        key,
        position: mesh.position.clone(),
        hearth: look.hearth.map((point) => turned(point, turn, mesh.position)),
        smoke: look.smoke.map((point) => turned(point, turn, mesh.position)),
        // Ten sam zegar, co przy pasku i przy rusztowaniu: budowa kończy się po
        // tej stronie, więc pytanie o nią też idzie z tego zegara.
        site: placed.readyIn - (now - since) / 1000 > 0,
      });
    }

    return all;
  }

  /**
   * Pola, na których trwa budowa albo rozbudowa — czyjakolwiek.
   *
   * Ten sam zegar, co przy rusztowaniu i dymie: plac kończy się po tej stronie.
   */
  sites(): Axial[] {
    const now = performance.now();
    const found: Axial[] = [];
    for (const { placed, since } of this.standing.values()) {
      if (placed.readyIn - (now - since) / 1000 > 0) found.push({ q: placed.q, r: placed.r });
    }
    return found;
  }

  /** Wszystkie budynki tego gracza, dla list wyboru w interfejsie. */
  ownedAll(owner: string): Placed[] {
    const mine: Placed[] = [];
    for (const { placed } of this.standing.values()) {
      if (placed.owner === owner) mine.push(placed);
    }
    return mine;
  }

  has(at: Axial): boolean {
    return this.occupied.has(axialKey(at.q, at.r));
  }

  /**
   * Które pola zajmuje ten budynek — jedno, a przy skrzydłach trzy.
   *
   * Liczone z katalogu, nie trzymane obok: lista pól jest wnioskiem z rodzaju
   * i osi, a jej kopia rozjechałaby się z serwerem w dniu, w którym ktoś
   * przestawi `wings` w pliku.
   */
  private cells(placed: Placed): Axial[] {
    const kind = buildingById(this.catalogue, placed.kind);
    const at = { q: placed.q, r: placed.r };
    return kind ? footprint(kind, at, placed.axis) : [at];
  }

  /**
   * Budynki stojące na tym polu, własnym polem naprzód.
   *
   * Jedna odpowiedź na pytanie „co tu stoi", o które pyta i panel pola, i dymek,
   * i reguła miejsca. Kolejność jest treścią: pole środkowe pyta się najczęściej
   * i odpowiada bez przechodzenia po skrzydłach.
   */
  private standingOn(at: Axial): { placed: Placed; mesh: THREE.Object3D; since: number }[] {
    if (!this.occupied.has(axialKey(at.q, at.r))) return [];

    const own = [];
    const winged = [];
    for (const held of this.standing.values()) {
      if (held.placed.q === at.q && held.placed.r === at.r) own.push(held);
      else if (this.cells(held.placed).some((spot) => spot.q === at.q && spot.r === at.r)) {
        winged.push(held);
      }
    }
    return [...own, ...winged];
  }

  /**
   * What stands on a cell, for a rule that needs to know whose and what kind.
   *
   * **Dok odpowiada swoją stocznią**, jak po tamtej stronie drutu: pole zajęte
   * przez skrzydło jest polem, na którym ten budynek stoi.
   */
  placedAt(at: Axial): Placed | undefined {
    return this.standingOn(at)[0]?.placed;
  }

  get count(): number {
    return this.standing.size;
  }

  /**
   * The ones still going up, and how far along they are.
   *
   * Counted locally from what the server said was left when it said it. A
   * building that has finished drops out of this, which is what takes its bar
   * off the screen — nothing has to be told that it is done.
   */
  raising(): { id: string; position: THREE.Vector3; base: THREE.Vector3; progress: number }[] {
    const now = performance.now();
    const raising: {
      id: string;
      position: THREE.Vector3;
      /** Ziemia pod budową — stąd idzie dym, gdy pasek wisi nad szczytem. */
      base: THREE.Vector3;
      progress: number;
    }[] = [];

    for (const { placed, mesh, since } of this.standing.values()) {
      if (placed.takes <= 0) continue;

      const left = Math.max(0, placed.readyIn - (now - since) / 1000);
      if (left <= 0) continue;

      const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
      raising.push({
        id: placed.id,
        position: new THREE.Vector3(
          mesh.position.x,
          mesh.position.y + shownOf(key, placed).height,
          mesh.position.z
        ),
        base: mesh.position.clone(),
        progress: Math.min(1, Math.max(0, 1 - left / placed.takes)),
      });
    }

    return raising;
  }

  /** How many of one kind this player has up — what a price is a curve over. */
  /**
   * How many of a kind that player has that are still below a rung.
   *
   * Twin of the server's `owned_below`, and it exists for the same reason the
   * price arithmetic is repeated on this side: the panel has to grey an entry
   * out before anybody clicks it.
   */
  /**
   * Ten sam budynek, inny szczebel.
   *
   * **Bryła bywa inna i to jest cała różnica wobec poprzedniej wersji tej
   * funkcji**, która wymieniała sam opis: obóz podniesiony **jest wioską** i ma
   * własny plik, więc zostawienie starej bryły znaczyłoby wioskę wyglądającą jak
   * obóz do przeładowania strony. Porównywane jest to, co się rysuje, a nie
   * numer szczebla — rodzaj, któremu awans nie zmienia wyglądu, nie płaci wtedy
   * za nic.
   *
   * `since` rusza od nowa, bo odliczanie rozbudowy biegnie od chwili, w której
   * o niej usłyszeliśmy.
   */
  replace(placed: Placed): boolean {
    const held = this.standing.get(placed.id);
    if (!held) return false;

    const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
    const now = shownOf(key, placed);
    const before = shownOf(key, held.placed);
    // **Rusztowanie liczy się tak samo, jak bryła.** Porównanie samych brył
    // przepuściłoby budynek, który skończył się budować bez zmiany szczebla —
    // a to jest dokładnie ta chwila, w której rusztowanie ma zejść.
    const reshaped = now.body !== before.body || now.scaffold !== before.scaffold;
    if (reshaped) {
      this.reshape(held, placed);
    }

    const bannerMoved = held.placed.banner !== placed.banner;
    held.placed = placed;
    if (!reshaped && bannerMoved) this.raiseBanner(placed.id, held, placed);
    held.since = performance.now();
    return true;
  }

  /**
   * Budowy, którym właśnie upłynął czas.
   *
   * **Podmiany dokonuje klient, własnym zegarem**, i to jest ta sama umowa, co
   * przy pasku postępu: serwer powiedział, ile zostało, a odliczanie należy do
   * tej strony. Serwer nie musi więc niczego rozsyłać w chwili, w której obóz
   * staje się wioską — a że `readyIn` i `raisingTo` jadą do **każdego**
   * w zasięgu, rusztowanie schodzi wszystkim w tej samej sekundzie, nie tylko
   * właścicielowi.
   *
   * Wołane co klatkę. Kosztuje przebieg po tym, co stoi w polu, i wychodzi
   * pierwszym `continue` na wszystkim, co się nie buduje.
   */
  tick(): void {
    const now = performance.now();

    for (const held of this.standing.values()) {
      if (!onSite(held.placed)) continue;
      if ((now - held.since) / 1000 < held.placed.readyIn) continue;

      // Wyzerowane `readyIn` jest tym, jak ten plik zapisuje u siebie, że
      // budowa się skończyła — drugi raz ten sam budynek się tu nie zatrzyma.
      // Serwer liczy to samo z własnego zegara, więc kolejna wiadomość o tym
      // budynku powie dokładnie to, co tu właśnie stanęło.
      const finished: Placed = {
        ...held.placed,
        level: held.placed.raisingTo ?? held.placed.level,
        raisingTo: undefined,
        readyIn: 0,
      };

      this.reshape(held, finished);
      held.placed = finished;
    }
  }

  /** Postaw bryłę od nowa: stara schodzi ze sceny, nowa siada na jej miejscu. */
  private reshape(
    held: { mesh: THREE.Object3D; picks: THREE.Mesh[] },
    placed: Placed
  ): void {
    this.strike(held.mesh, held.picks);
    const { mesh, picks } = this.build(placed);
    this.object.add(mesh);
    this.pickTargets.push(...picks);
    held.mesh = mesh;
    held.picks = picks;
    this.seat(placed, mesh);
    this.raiseBanner(placed.id, held, placed);
    this.cover(placed);
  }

  /** Zdejmij bryłę ze sceny i z celów kliknięcia. */
  private strike(mesh: THREE.Object3D, picks: THREE.Mesh[]): void {
    this.object.remove(mesh);
    for (const pick of picks) {
      const at = this.pickTargets.indexOf(pick);
      if (at !== -1) this.pickTargets.splice(at, 1);
    }
  }

  ownedBelow(owner: string, kind: number, level: number): number {
    let count = 0;
    for (const { placed } of this.standing.values()) {
      if (placed.owner === owner && placed.kind === kind && placed.level < level) count += 1;
    }
    return count;
  }

  ownedBy(owner: string, kind: number): number {
    let count = 0;
    for (const { placed } of this.standing.values()) {
      if (placed.owner === owner && placed.kind === kind) count += 1;
    }
    return count;
  }

  /** Re-seat everything: the ground under a building may have just arrived. */
  refresh(): void {
    for (const { placed, mesh } of this.standing.values()) this.seat(placed, mesh);
  }

  /** Let go of everything — the world was swapped, or the game is closing. */
  clear(): void {
    for (const { mesh, picks } of this.standing.values()) {
      this.strike(mesh, picks);
    }
    this.standing.clear();
    this.occupied.clear();
    this.covered.clear();
    this.covering.clear();
  }

  dispose(): void {
    this.clear();
    for (const material of this.materials.values()) material.dispose();
    this.materials.clear();
    // The shapes are shared with the ghost under the cursor, so they are let go
    // of where they live rather than by whichever holder shuts down first.
    disposeShapes();
  }

  private seat(placed: Placed, mesh: THREE.Object3D): void {
    const { x, z } = axialToWorld(placed.q, placed.r);
    const data = this.world.get(axialKey(placed.q, placed.r));
    mesh.position.set(x, data ? elevationToY(data.elevation) : mesh.position.y, z);
  }

  /**
   * Jedna bryła do postawienia na mapie.
   *
   * Model bywa z kilku części o różnych materiałach, więc powstaje z tego grupa;
   * sylwetka jest jedną siatką i zostaje nią. Kolor właściciela dostają
   * **wyłącznie sylwetki** — patrz nagłówek `BuildingShapes.ts`.
   */
  private build(placed: Placed): { mesh: THREE.Object3D; picks: THREE.Mesh[] } {
    const key = buildingById(this.catalogue, placed.kind)?.key ?? 'unknown';
    const shown = shownOf(key, placed);

    const parts = (look: BuildingLook) =>
      look.parts.map((part) => {
        const mesh = new THREE.Mesh(
          part.geometry,
          part.material ?? this.materialOf(placed.ownerName)
        );
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        return mesh;
      });

    const body = shown.body ? parts(shown.body) : [];

    // **Rusztowanie idzie do celów kliknięcia razem z bryłą**, a na pustym placu
    // budowy jest jedyną rzeczą, w którą da się trafić. Bez tego świeżo
    // postawiony obóz przestałby się otwierać pod prawym przyciskiem — a to jest
    // ten sam budynek, do którego trzeba donieść kłody.
    let scaffold: THREE.Mesh[] = [];
    let raised: THREE.Group | undefined;
    if (shown.scaffold) {
      scaffold = parts(shown.scaffold);
      // Skala idzie na obiekt, nie w geometrię: geometria jest współdzielona
      // przez wszystkie budowy w świecie, a każda z nich ma pod sobą co innego.
      raised = new THREE.Group();
      raised.add(...scaffold);
      raised.scale.setScalar(shown.scale);
    }

    const picks = [...body, ...scaffold];
    const pieces: THREE.Object3D[] = raised ? [...body, raised] : body;

    const mesh = pieces.length === 1 ? pieces[0] : new THREE.Group();
    if (mesh instanceof THREE.Group) mesh.add(...pieces);

    // Obrót siada na obiekcie, a nie w geometrii — ta jest współdzielona przez
    // wszystkie budynki tego rodzaju w świecie, tak samo jak przy skali
    // rusztowania.
    mesh.rotation.y = facingOf(placed);

    return { mesh, picks };
  }

  private materialOf(owner: string): THREE.MeshStandardMaterial {
    const color = colorFor(owner);
    const known = this.materials.get(color);
    if (known) return known;

    const material = new THREE.MeshStandardMaterial({ color, roughness: 0.8, flatShading: true });
    this.materials.set(color, material);
    return material;
  }
}
